import React from "react";
import LegalPage from "./LegalPage";
import { Link } from "react-router-dom";
import useContactInfo from "./useContactInfo";

/**
 * Refund Policy — mirrors the tone of vuplayer.pro's refund page but
 * adds the specifics we're required to disclose under the South African
 * Consumer Protection Act (7-day cooling off) and the Apple / Google
 * store rules.
 */
export default function Refund() {
  const { contactEmail } = useContactInfo();
  return (
    <LegalPage testId="refund-page" title="Refund Policy" updated="February 9, 2026">
      <p>
        We want you to be fully satisfied with your purchase. Before you
        buy a lifetime or yearly activation, we recommend claiming the{" "}
        <Link to="/trial">free 5-day trial</Link> first so you can
        evaluate whether ZA Player Pro works with your playlist on your
        specific device.
      </p>
      <p>
        If something goes wrong after purchase, our support team is
        happy to help you resolve it before considering a refund — most
        &ldquo;can&rsquo;t activate&rdquo; and &ldquo;stream won&rsquo;t
        play&rdquo; tickets are resolved within a few minutes on chat.
      </p>

      <h2>1. Refund windows</h2>
      <ul>
        <li>
          <strong>7-day cooling-off period</strong> — Buyers in South Africa
          have a statutory right under the Consumer Protection Act to
          cancel any online purchase within 7 days of receiving it, without
          giving a reason. You&rsquo;ll get 100&nbsp;% back to the same
          payment method.
        </li>
        <li>
          <strong>Post-cooling-off</strong> — Outside of that window, we
          will still consider a refund on a case-by-case basis if the app
          is provably not working on your device and our support team
          cannot get it working within a reasonable time.
        </li>
        <li>
          <strong>Lifetime keys</strong> — Because a lifetime key never
          expires, we strongly encourage you to try the trial first. Once
          the cooling-off period ends, lifetime purchases are non-refundable
          except where required by law.
        </li>
        <li>
          <strong>Renewals</strong> — A key renewal (extending an existing
          activation) is refundable only within 72 hours of purchase and
          only if the renewal has not yet been applied to the key.
        </li>
        <li>
          <strong>Reseller credit top-ups</strong> — Non-refundable once the
          credits have landed on your reseller balance. If none of the
          credits have been consumed you may email us to convert the
          top-up back to a payment refund on a case-by-case basis.
        </li>
      </ul>

      <h2>2. Payments made through Apple (iOS / tvOS)</h2>
      <p>
        In-app purchases made through Apple&rsquo;s App Store are handled
        entirely by Apple, not by us. Apple has its own refund process
        which you can start at{" "}
        <a href="https://reportaproblem.apple.com/">reportaproblem.apple.com</a>.
        Once Apple approves the refund the activation on our side will
        be cancelled automatically within 24 hours.
      </p>

      <h2>3. Payments made through Google Play (Android)</h2>
      <p>
        In-app purchases made through Google Play are handled by Google.
        Refund requests within 48 hours of purchase are usually approved
        automatically at{" "}
        <a href="https://play.google.com/store/account/orderhistory">play.google.com/store/account/orderhistory</a>.
        Beyond 48 hours contact us instead so we can review the case.
      </p>

      <h2>4. How to request a refund from us</h2>
      <p>
        For any purchase made directly on the website through PayFast,
        PayPal, or Stripe:
      </p>
      <ol>
        <li>
          Find your <strong>payment reference</strong>. It&rsquo;s on the
          success screen you saw right after paying, and it&rsquo;s
          included in the confirmation email we send you. It looks like{" "}
          <code>pay-af02-…-6b9c</code>.
        </li>
        <li>
          Note the <strong>MAC address</strong> shown on your
          activation screen (12 hex characters, e.g. <code>AA:BB:CC:DD:EE:FF</code>).
          If you never got to see the activation screen because the app
          crashed, tell us the make and model of your device instead.
        </li>
        <li>
          Email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
          with the reference, the MAC (or device model), and a one-line
          description of the problem. Or use the form at{" "}
          <Link to="/support">/support</Link>.
        </li>
      </ol>
      <p>
        We respond to refund requests within <strong>one working day</strong>.
        Approved refunds are processed back to the original payment
        method within <strong>5&ndash;10 working days</strong>, depending
        on your bank.
      </p>

      <h2>5. What isn&rsquo;t refundable</h2>
      <ul>
        <li>
          Activation keys that have already been consumed (used to sign
          in on a device) for more than 7 days.
        </li>
        <li>
          Purchases where the reason for the refund is that your
          m3u/Xtream provider stopped supplying content. ZA Player Pro
          is a player only — we don&rsquo;t control your provider.
        </li>
        <li>
          Purchases where the reason is that you changed your mind about
          the price. Trials exist for that reason.
        </li>
      </ul>

      <h2>6. Contact</h2>
      <p>
        For anything refund-related email{" "}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
        or use the ticket form at{" "}
        <Link to="/support">/support</Link>.
      </p>
    </LegalPage>
  );
}
